"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import { useState, type FormEvent } from "react";
import { SubmitButton } from "@/components/ui-custom/forms/form-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { metaTextClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
export interface SupportFormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Honeypot; only rendered on the public surface. */
  website: string;
}

/** Signed-in requester details. The ticket takes them from the session, so they are read-only. */
interface SupportIdentity {
  name: string;
  email: string;
}
export type SupportSubmitResult = {
  ok: true;
} | {
  ok: false;
  message: string;
};
type SupportFormProps = {
  onSubmit: (values: SupportFormValues) => Promise<SupportSubmitResult>;
} & ({
  surface: "public";
} | {
  surface: "app";
  identity: SupportIdentity;
  onCancel: () => void;
  onSuccess: () => void;
});
type SubmissionState = {
  kind: "idle";
} | {
  kind: "submitting";
} | {
  kind: "success";
} | {
  kind: "error";
  message: string;
};
const EMPTY_VALUES: SupportFormValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
  website: ""
};

/**
 * Support request form shared by the public `/suporte` page and the in-app support dialog.
 * The public surface collects contact details; the app surface shows the signed-in identity.
 */
export function SupportForm(props) {
  const $ = _c(51);
  const isPublic = props.surface === "public";
  let t0;
  if ($[0] !== isPublic || $[1] !== props.identity) {
    t0 = isPublic ? EMPTY_VALUES : {
      ...EMPTY_VALUES,
      ...props.identity
    };
    $[0] = isPublic;
    $[1] = props.identity;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const initialValues = t0;
  const [values, setValues] = useState(initialValues);
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "idle"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [submission, setSubmission] = useState(t1);
  const isSubmitting = submission.kind === "submitting";
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (field, value) => {
      setValues(current => ({
        ...current,
        [field]: value
      }));
      setSubmission(_temp);
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const updateValue = t2;
  let t3;
  if ($[5] !== initialValues || $[6] !== isSubmitting || $[7] !== props || $[8] !== values) {
    t3 = async event => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }
      setSubmission({
        kind: "submitting"
      });
      const result = await props.onSubmit(values);
      if (!result.ok) {
        setSubmission({
          kind: "error",
          message: result.message
        });
        return;
      }
      setValues(initialValues);
      if (props.surface === "app") {
        setSubmission({
          kind: "idle"
        });
        props.onSuccess();
        return;
      }
      setSubmission({
        kind: "success"
      });
    };
    $[5] = initialValues;
    $[6] = isSubmitting;
    $[7] = props;
    $[8] = values;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleSubmit = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_0 => updateValue("name", event_0.target.value);
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = !isPublic;
  let t6;
  if ($[11] !== isPublic || $[12] !== t5 || $[13] !== values.name) {
    t6 = <Input autoComplete="name" label="Nome" maxLength={100} minLength={2} name="name" onChange={t4} readOnly={t5} required={isPublic} value={values.name} />;
    $[11] = isPublic;
    $[12] = t5;
    $[13] = values.name;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_1 => updateValue("email", event_1.target.value);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !isPublic;
  let t9;
  if ($[16] !== isPublic || $[17] !== t8 || $[18] !== values.email) {
    t9 = <Input autoComplete="email" label="E-mail" maxLength={254} name="email" onChange={t7} readOnly={t8} required={isPublic} type="email" value={values.email} />;
    $[16] = isPublic;
    $[17] = t8;
    $[18] = values.email;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{t6}{t9}</div>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = !isPublic;
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event_2 => updateValue("subject", event_2.target.value);
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== values.subject) {
    t13 = <Input autoComplete="off" autoFocus={t11} label="Assunto" maxLength={120} minLength={5} name="subject" onChange={t12} required={true} value={values.subject} />;
    $[24] = t11;
    $[25] = values.subject;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event_3 => updateValue("message", event_3.target.value);
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== values.message) {
    t15 = <Textarea label="Mensagem" maxLength={4000} minLength={10} name="message" onChange={t14} required={true} rows={6} value={values.message} />;
    $[28] = values.message;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== isPublic || $[31] !== values.website) {
    t16 = isPublic ? <input aria-hidden="true" autoComplete="off" className="absolute -left-[9999px] h-px w-px opacity-0" name="website" onChange={event_4 => updateValue("website", event_4.target.value)} tabIndex={-1} type="text" value={values.website} /> : null;
    $[30] = isPublic;
    $[31] = values.website;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== submission.kind) {
    t17 = submission.kind === "success" ? <Alert variant="success"><AlertDescription>Mensagem enviada. Responderemos por e-mail assim que possível.</AlertDescription></Alert> : null;
    $[33] = submission.kind;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== submission.kind || $[36] !== submission.message) {
    t18 = submission.kind === "error" ? <Alert variant="destructive"><AlertDescription>{submission.message}</AlertDescription></Alert> : null;
    $[35] = submission.kind;
    $[36] = submission.message;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isSubmitting || $[39] !== props.onCancel || $[40] !== props.surface) {
    t19 = props.surface === "public" ? <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><p className={cn(metaTextClassName, "max-w-[40ch]")}>Ao enviar, você concorda que usemos esses dados apenas para responder ao seu atendimento.</p><SubmitButton className="w-full sm:w-auto" isSubmitting={isSubmitting} loadingText="Enviando..." size="sm" variant="marketing">Enviar mensagem<ArrowRight /></SubmitButton></div> : <DialogFooter><Button disabled={isSubmitting} onClick={props.onCancel} variant="ghost">Cancelar</Button><SubmitButton isSubmitting={isSubmitting} loadingText="Enviando...">Enviar mensagem</SubmitButton></DialogFooter>;
    $[38] = isSubmitting;
    $[39] = props.onCancel;
    $[40] = props.surface;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== handleSubmit || $[43] !== t10 || $[44] !== t13 || $[45] !== t15 || $[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19) {
    t20 = <form className="flex flex-col gap-4" onSubmit={handleSubmit}>{t10}{t13}{t15}{t16}{t17}{t18}{t19}</form>;
    $[42] = handleSubmit;
    $[43] = t10;
    $[44] = t13;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}
function _temp(current_0) {
  return current_0.kind === "idle" ? current_0 : {
    kind: "idle"
  };
}